const SCORE_LEVELS = [
  { val: 1, label: 'Minimal', desc: 'Negligible impact if realized' },
  { val: 2, label: 'Low',     desc: 'Minor impact, easy to mitigate' },
  { val: 3, label: 'Moderate',desc: 'Noticeable impact, manageable with standard controls' },
  { val: 4, label: 'High',    desc: 'Significant impact, requires explicit mitigation' },
  { val: 5, label: 'Critical',desc: 'Severe — safety, privacy, or regulatory breach risk' },
];
const RISK_DIMENSIONS = [
  { id: 'rf-safety',   label: 'Patient safety',     weight: 25, hint: 'Potential for direct patient harm if the system misbehaves.' },
  { id: 'rf-bias',     label: 'Bias &amp; fairness', weight: 20, hint: 'Disparate impact across race, ethnicity, gender, age, or payer.' },
  { id: 'rf-privacy',  label: 'Data privacy',        weight: 15, hint: 'PHI exposure risk, de-identification, consent, minimum necessary.' },
  { id: 'rf-clinical', label: 'Clinical validity',   weight: 15, hint: 'Scientific evidence for the clinical claim; local validation.' },
  { id: 'rf-cyber',    label: 'Cybersecurity',       weight: 15, hint: 'Attack surface, prompt injection, model poisoning, API hardening.' },
  { id: 'rf-reg',      label: 'Regulatory',          weight: 10, hint: 'Compliance gaps with FDA, HIPAA, ONC HTI-1, EU AI Act, state laws.' },
];

function scoreSegmentedControl(id, label, weight, hint, initialValue = 3) {
  const segments = SCORE_LEVELS.map(s => `
    <button type="button" class="score-segment score-segment-${s.val} ${s.val === initialValue ? 'active' : ''}"
            data-val="${s.val}" data-label="${s.label}" data-desc="${s.desc}"
            aria-label="${s.label}: ${s.desc}">${s.val}</button>`).join('');
  const initial = SCORE_LEVELS.find(s => s.val === initialValue) || SCORE_LEVELS[2];
  return `
    <div class="score-dimension">
      <div class="score-dimension-head">
        <span class="score-dimension-label">${label}</span>
        <span class="score-dimension-weight" title="${hint}">${weight}%</span>
      </div>
      <div class="score-segmented" data-score-target="${id}" role="radiogroup" aria-label="${label} score">
        ${segments}
      </div>
      <div class="score-scale-axis">
        <span>Minimal</span><span>Critical</span>
      </div>
      <div class="score-meaning score-meaning-${initialValue}" data-meaning-for="${id}">
        <strong>${initial.label}</strong> — ${initial.desc}
      </div>
      <input type="hidden" id="${id}" value="${initialValue}">
    </div>`;
}

function _consumeFilter(pageKey) {
  const nf = window._navFilter;
  if (!nf || nf.page !== pageKey) return {};
  window._navFilter = null;
  return nf.filter || {};
}

export const risk = {
  async riskAssessments() {
    const activeFilter = _consumeFilter('risk-assessments');
    let data = { data: [] };
    try { data = await API.getRiskAssessments(activeFilter); } catch (e) { /* empty */ }

    const filterBanner = Object.keys(activeFilter).length ? `
      <div class="filter-banner">
        <span>Showing results filtered by
          ${Object.entries(activeFilter).map(([k, v]) => `<strong>${k.replace(/_/g, ' ')}: ${v}</strong>`).join(', ')}
        </span>
        <button class="btn btn-sm btn-ghost" data-clear-filter="risk-assessments">Clear filter</button>
      </div>` : '';

    return `
      <div class="page-header">
        <div><h2>Risk Assessments</h2><p>Multi-dimensional risk evaluations aligned with NIST AI RMF</p></div>
        <button class="btn btn-primary" id="btn-add-risk">+ New Risk Assessment</button>
      </div>
      ${filterBanner}
      <div class="card">
        <div class="table-container">
          <table>
            <thead><tr><th>AI System</th><th>Type</th><th>Safety</th><th>Bias</th><th>Privacy</th><th>Clinical</th><th>Cyber</th><th>Regulatory</th><th>Overall</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              ${data.data.length > 0 ? data.data.map(r => `
                <tr>
                  <td><strong>${r.asset_name}</strong></td>
                  <td>${r.assessment_type}</td>
                  <td>${r.patient_safety_score || '-'}/5</td>
                  <td>${r.bias_fairness_score || '-'}/5</td>
                  <td>${r.data_privacy_score || '-'}/5</td>
                  <td>${r.clinical_validity_score || '-'}/5</td>
                  <td>${r.cybersecurity_score || '-'}/5</td>
                  <td>${r.regulatory_score || '-'}/5</td>
                  <td>${App.badge(r.overall_risk_level)}</td>
                  <td>${App.badge(r.status)}</td>
                  <td>
                    ${r.status === 'draft' || r.status === 'in_review' ? `
                      <div class="btn-group">
                        <button class="btn btn-sm btn-primary" data-approve-risk="${r.id}" data-risk-tier="${r.overall_risk_level || ''}">Approve</button>
                        <button class="btn btn-sm btn-outline" data-reject-risk="${r.id}">Reject</button>
                      </div>
                    ` : r.status === 'approved' ? `
                      <span class="text-muted" style="font-size:12px">Approved</span>
                    ` : r.status === 'rejected' ? `
                      <span class="text-muted" style="font-size:12px">Rejected</span>
                    ` : '—'}
                  </td>
                </tr>
              `).join('') : '<tr><td colspan="11" class="empty-state">No risk assessments yet</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>`;
  },

  async showApproveRiskForm(assessmentId, computedTier) {
    // Default residual risk mirrors the computed overall tier unless the
    // reviewer says otherwise. Anything in {critical, high, moderate, low}.
    const defaultTier = ['critical', 'high', 'moderate', 'low'].includes(computedTier) ? computedTier : 'moderate';

    App.openModal('Approve Risk Assessment', `
      <form id="approve-risk-form">
        <div class="attestation-box" style="margin-bottom:var(--sp-5)">
          <p style="margin-bottom:var(--sp-3);color:var(--text);font-weight:500">
            By approving, you confirm on behalf of <strong>${API.tenant?.name || 'this organization'}</strong> that:
          </p>
          <ul>
            <li>The 6-dimension risk scoring has been reviewed by an authorized approver.</li>
            <li>The residual risk (after planned mitigations) is acceptable for this use case.</li>
            <li>Any conditions recorded below must be satisfied for the approval to remain valid.</li>
          </ul>
        </div>

        <div class="form-group"><label>Residual risk level <span class="required">*</span></label>
          <select id="ar-residual" required>
            <option value="low"      ${defaultTier === 'low' ? 'selected' : ''}>Low — acceptable with standard monitoring</option>
            <option value="moderate" ${defaultTier === 'moderate' ? 'selected' : ''}>Moderate — acceptable with defined mitigations</option>
            <option value="high"     ${defaultTier === 'high' ? 'selected' : ''}>High — acceptable only with committee approval + compensating controls</option>
            <option value="critical" ${defaultTier === 'critical' ? 'selected' : ''}>Critical — accepted risk, requires executive sign-off</option>
          </select>
          <small>This is the risk that remains after the mitigations in the recommendations and any conditions below are in place.</small>
        </div>

        <div class="form-group"><label>Residual risk justification <span class="required">*</span></label>
          <textarea id="ar-justification" required placeholder="Why is this residual risk acceptable? Reference the mitigations and the authority that reviewed the decision."></textarea>
          ${window.ForgeML ? ForgeML.button('ar-justification', 'residual_risk_justification') : ''}
        </div>

        <div class="form-group"><label>Review notes (optional)</label>
          <textarea id="ar-notes" placeholder="Free-form comments for the approval record."></textarea>
        </div>

        <div class="form-group"><label>Approval memo evidence (optional)</label>
          <input type="text" id="ar-memo" placeholder="Evidence ID of the signed approval memo, if already uploaded">
          <small>Upload the signed approval memo under Evidence first, then paste the evidence ID here. Leave blank and add later if the memo is pending.</small>
        </div>
      </form>
    `, '<button class="btn btn-outline" data-modal-close>Cancel</button><button class="btn btn-primary" id="ar-submit">Approve &amp; record</button>');
    document.querySelectorAll('[data-modal-close]').forEach(b => b.addEventListener('click', () => App.closeModal()));
    if (window.ForgeML) ForgeML.wire();
    document.getElementById('ar-submit').addEventListener('click', async () => {
      const justification = document.getElementById('ar-justification').value.trim();
      if (!justification) { App.toast('Residual risk justification is required', 'error'); return; }
      try {
        await API.approveRiskAssessment(assessmentId, {
          approved: true,
          residual_risk_level: document.getElementById('ar-residual').value,
          residual_risk_justification: justification,
          review_notes: document.getElementById('ar-notes').value || null,
          approval_memo_evidence_id: document.getElementById('ar-memo').value || null,
        });
        App.closeModal();
        App.toast('Risk assessment approved', 'success');
        App.navigate('risk-assessments');
      } catch (err) { App.toast(err.message, 'error'); }
    });
  },

  async showRejectRiskForm(assessmentId) {
    App.openModal('Reject Risk Assessment', `
      <form id="reject-risk-form">
        <div class="alert alert-warning" style="margin-bottom:var(--sp-4)">
          Rejecting returns the assessment to the asset owner for remediation. The asset cannot be moved to deployed status until a new assessment is submitted and approved.
        </div>
        <div class="form-group"><label>Rejection reason <span class="required">*</span></label>
          <textarea id="rr-notes" required placeholder="What gaps or risks led to rejection? What must change before resubmission? Example: Bias testing was performed only on adult population; pediatric cohort required per Colorado SB 21-169 before we can approve."></textarea>
        </div>
      </form>
    `, '<button class="btn btn-outline" data-modal-close>Cancel</button><button class="btn btn-danger" id="rr-submit">Reject assessment</button>');
    document.querySelectorAll('[data-modal-close]').forEach(b => b.addEventListener('click', () => App.closeModal()));
    document.getElementById('rr-submit').addEventListener('click', async () => {
      const notes = document.getElementById('rr-notes').value.trim();
      if (!notes) { App.toast('Rejection reason is required', 'error'); return; }
      try {
        await API.approveRiskAssessment(assessmentId, {
          approved: false,
          review_notes: notes,
        });
        App.closeModal();
        App.toast('Risk assessment rejected', 'success');
        App.navigate('risk-assessments');
      } catch (err) { App.toast(err.message, 'error'); }
    });
  },

  async showRiskForm() {
    let assets = [];
    try { assets = (await API.getAssets()).data || []; } catch { /* fall back to empty list */ }
    if (!assets.length) {
      App.openModal('New Risk Assessment',
        `<div class="empty-state"><p>You need to register at least one AI system before running a risk assessment.</p><button class="btn btn-primary" data-goto-assets>Go to AI Asset Registry</button></div>`,
        '<button class="btn btn-outline" data-modal-close>Close</button>');
      document.querySelectorAll('[data-modal-close]').forEach(b => b.addEventListener('click', () => App.closeModal()));
      document.querySelectorAll('[data-goto-assets]').forEach(b => b.addEventListener('click', () => { App.closeModal(); App.navigate('ai-assets'); }));
      return;
    }
    const assetOptions = assets.map(a =>
      `<option value="${a.id}">${a.name}${a.version ? ' v' + a.version : ''} — ${a.risk_tier} risk${a.phi_access ? ' · PHI' : ''}</option>`
    ).join('');

    App.openModal('New Risk Assessment', `
      <form id="risk-form">
        <div class="form-group"><label>AI System <span class="required">*</span></label>
          <select id="rf-asset" required>
            <option value="">Select an AI system…</option>
            ${assetOptions}
          </select>
          <small>Only systems already in your registry appear here. <a href="#" data-goto-assets>Register a new AI asset</a> first if it's not listed.</small>
        </div>
        <div class="form-group"><label>Assessment type <span class="required">*</span></label>
          <select id="rf-type"><option value="initial">Initial</option><option value="periodic">Periodic</option><option value="triggered">Triggered</option><option value="pre_deployment">Pre-Deployment</option></select>
        </div>
        <div class="score-dimensions-head">
          <div>
            <h4 class="modal-section-head" style="margin:0 0 4px 0">Risk dimensions</h4>
            <p class="text-muted" style="font-size:12px;margin:0">Score each dimension 1 (minimal) to 5 (critical). The overall risk tier is computed from a weighted average.</p>
          </div>
        </div>
        <div class="score-dimensions">
          ${RISK_DIMENSIONS.map(d => scoreSegmentedControl(d.id, d.label, d.weight, d.hint, 3)).join('')}
        </div>
        <div class="form-group" style="margin-top:var(--sp-5)"><label>Recommendations</label>
          <textarea id="rf-recs" placeholder="Mitigation steps, required controls, conditions for approval…"></textarea>
        </div>
      </form>
    `, '<button class="btn btn-outline" data-modal-close>Cancel</button><button class="btn btn-primary" id="rf-submit">Create assessment</button>');
    document.querySelectorAll('[data-modal-close]').forEach(b => b.addEventListener('click', () => App.closeModal()));
    document.querySelectorAll('[data-goto-assets]').forEach(b => b.addEventListener('click', (ev) => { ev.preventDefault(); App.closeModal(); App.navigate('ai-assets'); }));

    // Wire the segmented score controls
    document.querySelectorAll('.score-segmented').forEach(group => {
      const targetId = group.getAttribute('data-score-target');
      const hiddenInput = document.getElementById(targetId);
      const meaningEl = document.querySelector(`[data-meaning-for="${targetId}"]`);
      group.querySelectorAll('.score-segment').forEach(btn => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('.score-segment').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          const val = btn.getAttribute('data-val');
          hiddenInput.value = val;
          if (meaningEl) {
            meaningEl.className = `score-meaning score-meaning-${val}`;
            meaningEl.innerHTML = `<strong>${btn.getAttribute('data-label')}</strong> — ${btn.getAttribute('data-desc')}`;
          }
        });
      });
    });

    document.getElementById('rf-submit').addEventListener('click', async () => {
      const assetId = document.getElementById('rf-asset').value;
      if (!assetId) { App.toast('Please select an AI system', 'error'); return; }
      try {
        await API.createRiskAssessment({
          ai_asset_id: assetId,
          assessment_type: document.getElementById('rf-type').value,
          patient_safety_score: parseInt(document.getElementById('rf-safety').value),
          bias_fairness_score: parseInt(document.getElementById('rf-bias').value),
          data_privacy_score: parseInt(document.getElementById('rf-privacy').value),
          clinical_validity_score: parseInt(document.getElementById('rf-clinical').value),
          cybersecurity_score: parseInt(document.getElementById('rf-cyber').value),
          regulatory_score: parseInt(document.getElementById('rf-reg').value),
          recommendations: document.getElementById('rf-recs').value || null,
        });
        App.closeModal();
        App.toast('Risk assessment created', 'success');
        App.navigate('risk-assessments');
      } catch (err) { App.toast(err.message, 'error'); }
    });
  },
};
